import type { TargetedEvent } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import cn from 'classnames';
import type { MediaGalleryConfig } from '../../MediaGallery.config.ts';
import type { MediaGalleryItem } from '../../MediaGallery.tsx';
import styles from './ZoomImage.module.scss';
import Image from './Image.tsx';
import Video from './Video.tsx';
import { hasNext, hasPrev } from '../../MediaGallery.utils.ts';
import Chevron from '../Chevron/Chevron.tsx';
import { lastEventSource } from '@/widgets/hooks/useVideoTracking.tsx';

export type ZoomImageProps = {
  config: MediaGalleryConfig;
  onClick: (e: MouseEvent) => void;
  /** Event triggered when the image is magnified */
  onMagnify?: () => void;
  /** Event triggered when a gallery image is loaded (not magnified image) */
  onImageLoad?: () => void;
  advanceSlideBy: (
    event: TargetedEvent<HTMLButtonElement>,
    direction: 1 | -1
  ) => void;
  currentIndex: number;
  items: MediaGalleryItem[];
  item: MediaGalleryItem;
  /** Magnification will only be enabled if there is a hi-res image given! */
  hiresImage?: MediaGalleryItem;
  isZoomed: boolean;
  isClosing?: boolean;
};
export default function ZoomImage({
  onClick,
  onMagnify,
  onImageLoad,
  advanceSlideBy,
  config,
  currentIndex,
  items,
  item,
  hiresImage,
  isZoomed,
  isClosing,
  ...rest
}: ZoomImageProps) {
  const imageRef = useRef<HTMLImageElement>(null);
  const wrapperRef = useRef<HTMLButtonElement>(null);

  const clickedCoords = useRef<{
    x: number;
    y: number;
  }>({
    x: 0,
    y: 0
  });

  const { wrapAroundOnModalView, translations: t, hasReducedMotion } = config;
  const arrowPrevIsEnabled = hasPrev(
    items,
    currentIndex,
    Boolean(wrapAroundOnModalView)
  );
  const arrowNextIsEnabled = hasNext(
    items,
    currentIndex,
    Boolean(wrapAroundOnModalView)
  );

  /* v8 ignore start */
  function handleClick(event: MouseEvent) {
    if (!wrapperRef.current) return;
    if (!hiresImage) return; // if no hi-res image, don't magnify
    onClick(event);
  }
  /* v8 ignore end */

  /* v8 ignore start */
  function handleMouseMove(event: MouseEvent) {
    if (!wrapperRef.current || !imageRef.current || !isZoomed) return;

    const { offsetX, offsetY, clientX, clientY } = event;
    if (offsetX < 0 || offsetY < 0) return;

    scrollToImage(clientX, clientY);
  }
  /* v8 ignore end */

  function handleMouseUp(event: MouseEvent) {
    if (!wrapperRef.current || !imageRef.current) return;
    const { scrollWidth: maxScrollLeft, scrollHeight: maxScrollTop } =
      wrapperRef.current;
    const { offsetX, offsetY } = event;
    if (offsetX < 0 || offsetY < 0) return;

    // how far we are across the image - using the full screen: 0 -> 1
    const posXPercent = offsetX / maxScrollLeft;
    // how far we are down the image - using the full screen: 0 -> 1
    const posYPercent = offsetY / maxScrollTop;

    // remember clicked coords for later
    clickedCoords.current = {
      x: posXPercent,
      y: posYPercent
    };
  }

  /**
   * When the user clicks the original image, it is magnified for them, but we have to scroll
   * to a position of the magnified image that corresponds to the same visual position of where
   * their pointer was, taking into consideration the coordinates of their original click.
   */
  function scrollToImage(clientX?: number, clientY?: number) {
    if (!wrapperRef.current) return;

    // how far we are across the image - using the full screen
    const { offsetWidth, offsetHeight, scrollWidth, scrollHeight } =
      wrapperRef.current;

    // how far across the screen is the pointer: 0 -> 1
    const posXPercent = clientX
      ? clientX / offsetWidth
      : clickedCoords.current.x;
    // how far down the screen is the pointer: 0 -> 1
    const posYPercent = clientY
      ? clientY / offsetHeight
      : clickedCoords.current.y;

    // get the scrollbar px limits
    const maxScrollX = scrollWidth - offsetWidth;
    const maxScrollY = scrollHeight - offsetHeight;

    requestAnimationFrame(() => {
      if (!wrapperRef.current) return;
      // scroll to a percentage across the image, taking into account the image portal width
      wrapperRef.current.scrollLeft = maxScrollX * posXPercent;
      wrapperRef.current.scrollTop = maxScrollY * posYPercent;
    });
  }

  const handleImageLoaded = () => {
    if (!imageRef.current) return;
    // eslint-disable-next-line no-console
    if (!isClosing && onImageLoad) {
      // render occurs
      onImageLoad();
    }
  };

  useEffect(() => {
    if (isZoomed) {
      if (onMagnify) {
        onMagnify();
      }
      scrollToImage();
    }
  }, [isZoomed]);

  if (isClosing) {
    return null;
  }
  const chevrons = (
    <div class={styles.chevronsBox}>
      <Chevron
        type="prev"
        onClick={(event) => {
          event.stopPropagation();
          advanceSlideBy(event, -1);
          lastEventSource.value = 'arrow scroll';
        }}
        a11yLabel={t.prevLabel}
        disabled={!arrowPrevIsEnabled}
      />
      <Chevron
        type="next"
        onClick={(event) => {
          event.stopPropagation();
          advanceSlideBy(event, 1);
          lastEventSource.value = 'arrow scroll';
        }}
        a11yLabel={t.nextLabel}
        disabled={!arrowNextIsEnabled}
      />
    </div>
  );

  return item.type === 'VIDEO' ? (
    <div class={styles.videoContainer}>
      <Video
        url={item.url}
        noVideoSupportLabel=""
        playPauseButtonLabel=""
        hasReducedMotion={hasReducedMotion}
      />
      {chevrons}
    </div>
  ) : (
    <button
      ref={wrapperRef}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onClick={handleClick} // Note: fires after onMouseUp event
      type="button"
      tabIndex={0}
      aria-label={item.alt}
      class={cn(styles.fullImageContainer, styles.withThumbnails, {
        [styles.magnifiedContainer]: isZoomed,
        [styles.supportsMagnification]: hiresImage
      })}
      {...rest}
    >
      <Image
        imageRef={imageRef}
        alt={item.alt}
        src={isZoomed && hiresImage ? hiresImage.url : item.url}
        onLoad={handleImageLoaded}
        class={cn(styles.image, {
          [styles.magnifiedImage]: isZoomed
        })}
      />
      {!isZoomed && chevrons}
    </button>
  );
}
